// platform/src/components/mw/MwSection.tsx
//
// Wrapper de sección con max-width y padding consistentes.
// Tokens consumidos desde styles/mw.css.

import type { ReactNode } from 'react'

interface MwSectionProps {
  children: ReactNode
  /**
   * Si el fondo llega a los bordes de la pantalla.
   *
   * Las secciones viven dentro del contenedor de pagina (1200px), asi
   * que su fondo tambien medía 1200: en un monitor de 1800 se veia un
   * rectangulo gris flotando en medio del blanco en vez de una banda.
   * Con esto el color llega de borde a borde y el contenido se queda
   * donde estaba, alineado con el resto de la pagina.
   */
  bleed?: boolean
  background?: 'white' | 'section'
  padding?: 'hero' | 'default' | 'compact'
  width?: 'narrow' | 'wide'
  className?: string
  id?: string
}

const BG_MAP: Record<NonNullable<MwSectionProps['background']>, string> = {
  white: 'var(--mw-surface)',
  section: 'var(--mw-surface-section)',
}

const PADDING_MAP: Record<NonNullable<MwSectionProps['padding']>, string> = {
  hero: 'py-24 md:py-40',
  default: 'py-16 md:py-24',
  compact: 'py-12 md:py-16',
}

const WIDTH_MAP: Record<NonNullable<MwSectionProps['width']>, string> = {
  narrow: 'max-w-4xl',
  wide: 'max-w-6xl',
}

export function MwSection({
  children,
  background = 'white',
  padding = 'default',
  width = 'wide',
  bleed = false,
  className = '',
  id,
}: MwSectionProps) {
  return (
    <section
      id={id}
      className={`${PADDING_MAP[padding]} ${bleed ? 'mw-bleed' : ''} ${className}`}
      style={{ background: BG_MAP[background] }}
    >
      <div className={`mx-auto ${WIDTH_MAP[width]} px-6 md:px-10`}>{children}</div>
    </section>
  )
}
